import React, { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import PropTypes from "prop-types";
import { yupResolver } from "@hookform/resolvers/yup";
import { Col, Row } from 'react-styled-flexboxgrid';
import Button from '../../components/Button';
import ButtonGroup from '../../components/ButtonGroup';
import Divider from '../../components/Divider';
import Form from '../../components/Form';
import Icon from '../../components/Icon';
import Paper from '../../components/Paper';
import Typography from '../../components/Typography';
import useTheme from '../../hooks/useTheme';
import JobShortDesc from '../../pages/Dashboard/components/JobShortDesc';
import { makeStyles } from '../../utils/styles';
import { useForm } from 'react-hook-form';
import Rating from '../../components/Rating';
import Table from '../../components/Table';
import Switch from '../../components/Switch';
import Input from '../../components/Input';
import RatingStars from '../../components/RatingStars';
import User from '../../components/User';
import { add, differenceInDays, differenceInMinutes, format, fromUnixTime, getUnixTime, isSameDay } from 'date-fns';
import { findByID } from '../../utils/helpers';
import Box from '../../components/Box';
import Loader from '../../components/Loader';
import ReactDatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((css) => ({
    paper: css`
        position: relative;
        overflow: hidden;
        padding: 30px 30px 15px 30px;
        width: 1040px;
    `,
    dividerHeader: css`
        margin-top: 25px;
        margin-bottom: 20px;
    `,
    divider: css`
        margin: 20px 0;
    `,
    inputTime: css`
        max-width: 70px;
        margin-bottom: 0px;
    `,
    boxRateForAll: css`
        margin-top: 25px;
        margin-bottom: 10px;
    `,
    boxTimeEnd: css`
        max-width: 70px;
        margin-top: 14px;
    `,
    typographyTimeEnd: css`
        margin-top: 2px;
        height: 12px;
        text-align: center;
        font-size: 12px;
        line-height: 1em;
    `,
}));

const schema = yup.object().shape({
    applications: yup.array(),
});

const FormJobSettlement = ({ loading, job, onClickCancel, onSubmit }) => {
    const { t } = useTranslation()
    const styles = useStyles();
    const theme = useTheme();

    const [rateForAll, setRateForAll] = useState(0);

    const { register, handleSubmit, setValue, getValues, watch } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            applications: [],
        },
    });

    useEffect(() => {
        register("applications", []);
    }, [register]);

    useEffect(() => {
        if (job?.applications) {
            setValue(
                "applications",
                job?.applications.map((application) => ({
                    id: application?.id,
                    is_penalty: true,
                    user_candidate_id: application?.user_candidate_id,
                    job_id: job?.id,
                    user_time_start: job.is_settled
                        ? application?.user_time_start
                        : job?.time_start,
                    user_time_end: job.is_settled
                        ? application?.user_time_end
                        : job?.time_end,
                    candidate_ranking: application?.candidate_ranking || 0,
                }))
            );
        }
    }, [job, setValue]);


    const jobTimeStart = useMemo(() => fromUnixTime(job?.time_start), [job]);

    const jobTimeEnd = useMemo(() => fromUnixTime(job?.time_end), [job]);

    const watchApplications = watch("applications");

    // const handleClickStarForAll = value => setRateForAll(value);
    const handleClickStarForAll = (value) => {
        setRateForAll(value);
        const applications = getValues("applications");
        setValue(
            "applications",
            applications.map((application) => {
                return {
                    ...application,
                    candidate_ranking: value,
                };
            })
        );
    };

    const handleChangeIsPenalty = (id) => (e) => {
        const applications = getValues("applications");
        setValue(
            "applications",
            applications.map((application) => {
                if (application.id === id) {
                    return {
                        ...application,
                        is_penalty: e.target.checked,
                    };
                }
                return application;
            })
        );
    };

    const hadnleChangeTimeStart = (id) => (date) => {
        if (date) {
            const applications = getValues("applications");
            setValue(
                "applications",
                applications.map((application) => {
                    if (application.id === id) {
                        const currTimeEnd = fromUnixTime(
                            application?.user_time_end
                        );
                        const diffMinutes = differenceInMinutes(
                            currTimeEnd,
                            date
                        );
                        const diffDays = differenceInDays(currTimeEnd, date);
                        if (diffMinutes < 0 && diffDays === 0) {
                            return {
                                ...application,
                                user_time_end: getUnixTime(
                                    add(currTimeEnd, { days: 1 })
                                ),
                                user_time_start: getUnixTime(date),
                            };
                        } else {
                            return {
                                ...application,
                                user_time_start: getUnixTime(date),
                            };
                        }
                    }
                    return application;
                })
            );
        }
    };

    const hadnleChangeTimeEnd = (id) => (date) => {
        if (date) {
            const applications = getValues("applications");
            setValue(
                "applications",
                applications.map((application) => {
                    if (application.id === id) {
                        const currTimeStart = fromUnixTime(
                            application?.user_time_start
                        );
                        const diffMinutes = differenceInMinutes(
                            date,
                            currTimeStart
                        );
                        const diffDays = differenceInDays(date, currTimeStart);
                        if (diffMinutes < 0 && diffDays === 0) {
                            return {
                                ...application,
                                user_time_end: getUnixTime(
                                    add(date, { days: 1 })
                                ),
                            };
                        } else {
                            return {
                                ...application,
                                user_time_end: getUnixTime(date),
                            };
                        }
                    }
                    return application;
                })
            );
        }
    };

    const handleClickStar = (id) => (value) => {
        const applications = getValues("applications");
        setValue(
            "applications",
            applications.map((application) => {
                if (application.id === id) {
                    return {
                        ...application,
                        candidate_ranking: value,
                    };
                }
                return application;
            })
        );
    };

    const columns = useMemo(
        () => [
            {
                Header: t('Pracownik'),
                accessor: 'application_original.username',
                Cell: ({ row }) => {
                    return (
                        <User
                            user={
                                row.original?.application_original
                                    ?.user_candidate
                            }
                        />
                    );
                },
            },
            {
                Header: t('Ranking pracownika'),
                accessor: 'application_original.user_candidate_rankings',
                Cell: ({ cell: { value } }) => (
                    <Rating rate={value} />
                )
            },
            {
                Header: t('Obecność'),
                accessor: 'is_penalty',
                Cell: ({ row, cell: { value } }) => {
                    return (
                        <Switch
                            checked={value}
                            onChange={handleChangeIsPenalty(row.original?.id)}
                        />
                    );
                },
            },
            {
                Header: t('Rozpoczęcie pracy'),
                accessor: 'user_time_start',
                Cell: ({ row, cell: { value } }) => {
                    const timeStart = fromUnixTime(value);
                    return (
                        <ReactDatePicker
                            selected={timeStart}
                            onChange={hadnleChangeTimeStart(row.original?.id)}
                            showTimeSelect
                            showTimeSelectOnly
                            timeFormat="HH:mm"
                            timeIntervals={30}
                            dateFormat="HH:mm"
                            disabled={!row.original?.is_penalty}
                            popperPlacement="top-start"
                            timeCaption="godzina"
                            locale="pl"
                            customInput={
                                <Input
                                    type="text"
                                    customStyle={styles.inputTime}
                                />
                            }
                        />
                    );
                },
            },
            {
                Header: t('Zakończenie pracy'),
                accessor: 'user_time_end',
                Cell: ({ row, cell: { value } }) => {
                    const timeEnd = fromUnixTime(value);
                    const timeStart = fromUnixTime(
                        row.original?.user_time_start
                    );
                    return (
                        <Box customStyle={styles.boxTimeEnd}>
                            <ReactDatePicker
                                selected={timeEnd}
                                onChange={hadnleChangeTimeEnd(row.original?.id)}
                                minDate={
                                    differenceInMinutes(
                                        add(timeEnd, { days: -1 }),
                                        timeStart
                                    ) < 0
                                        ? add(jobTimeStart, { days: 1 })
                                        : jobTimeStart
                                }
                                maxDate={add(jobTimeEnd, { days: 1 })}
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={30}
                                dateFormat="HH:mm"
                                disabled={!row.original?.is_penalty}
                                popperPlacement="top-start"
                                timeCaption="godzina"
                                locale="pl"
                                customInput={
                                    <Input
                                        type="text"
                                        customStyle={styles.inputTime}
                                    />
                                }
                            />
                            <Typography customStyle={styles.typographyTimeEnd}>
                                {!isSameDay(timeEnd, timeStart) &&
                                    format(timeEnd, "dd.MM.yyyy")}
                            </Typography>
                        </Box>
                    );
                },
            },
            {
                Header: t('Ocena pracownika'),
                accessor: 'candidate_ranking',
                Cell: ({ row, cell: { value } }) => {
                    return (
                        <RatingStars
                            interactive
                            rate={value}
                            onClickStar={handleClickStar(row.original?.id)}
                        />
                    );
                },
            },
        ],
        // eslint-disable-next-line
        [jobTimeEnd]
    );

    const data = useMemo(() => {
        return watchApplications.map((application) => {
            const application_original = findByID(
                job?.applications,
                application?.id
            );
            return {
                ...application,
                application_original,
            };
        });
    }, [job, watchApplications]);

    return (
        <Paper customStyle={styles.paper}>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Typography as="h1">
                    {t("Rozliczenie pracowników")}
                </Typography>
                <Divider customStyle={styles.dividerHeader} />
                <JobShortDesc job={job} showPlaceLogo />
                <Divider customStyle={styles.divider} />
                <Box customStyle={styles.boxRateForAll}>
                    <Row end="xs">
                        <Col>
                            <Typography>{t("Wystaw ocenę wszystkim pracownikom")}</Typography>
                        </Col>
                        <Col>
                            <RatingStars
                                interactive
                                rate={rateForAll}
                                onClickStar={handleClickStarForAll}
                            />
                        </Col>
                    </Row>
                </Box>
                <Table
                    columns={columns}
                    data={data}
                    hideFooter
                    guttersCell="small"
                />
                <Row end="xs">
                    <Col>
                        <ButtonGroup>
                            <Button
                                variant="text"
                                onClick={onClickCancel}
                                startIcon={
                                    <Icon
                                        name="close"
                                        width={12}
                                        height={12}
                                        fill={theme.colors.purple}
                                    />
                                }
                            >
                                {t("ANULUJ")}
                            </Button>
                            <Button
                                type="submit"
                                startIcon={
                                    <Icon
                                        name="order"
                                        width={12}
                                        height={12}
                                        fill={theme.colors.white}
                                    />
                                }
                                disabled={loading}
                            >
                                {t("ZAPISZ ROZLICZENIE")}
                            </Button>
                        </ButtonGroup>
                    </Col>
                </Row>
            </Form>
            <Loader show={loading} />
        </Paper>
    );
};

FormJobSettlement.propTypes = {
    loading: PropTypes.bool,
    onSubmit: PropTypes.func,
    onClickCancel: PropTypes.func,
    job: PropTypes.object,
};

export default FormJobSettlement;
